import React, { useState, FormEvent, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { FormGroup, InputGroup, Button, Tabs, Tab, Checkbox, Collapse, HTMLSelect, HTMLTable, ControlGroup, Classes, MenuItem, Alert } from "@blueprintjs/core";

import { IconNames } from "@blueprintjs/icons";

import { AxiosInstance } from 'axios';

import * as utils from "./utils";

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next';

import { Vendor } from './Types'

interface VendorViewProps {
    vend: Vendor,
    baseUrl?: string,
    token?: string | null,
    onEdit?: () => void,
    onNew?: () => void,
    onDelete?: (vend: Vendor) => void,
}

function VendorView({ vend = {} as Vendor, baseUrl = "", token = "", onEdit = () => { }, onNew = () => { }, onDelete = () => {} }: VendorViewProps) {
    const { t, i18n } = useTranslation();
    const [isOpenDelete, setIsOpenDelete] = useState(false)

    if (!("vendCode" in vend)) return null

    return (
        <div>
            <Alert icon="trash" intent="danger" confirmButtonText={t('button_delete')} isOpen={isOpenDelete} onConfirm={() => {onDelete(vend); setIsOpenDelete(false)}} onCancel={() => setIsOpenDelete(false)} cancelButtonText={t('button_no')} canEscapeKeyCancel={true} canOutsideClickCancel={true}>
                <p>{t('confirm_delete_vendor', {vend})}</p>
            </Alert>
            <div className="Row">
                <Button className="Col-1-1" icon="add" onClick={() => onNew()}>{t('new')}</Button>
                <Button className="Col-2-1" icon="edit" onClick={() => onEdit()}>{t('edit')}</Button>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('vendor')}>
                    <InputGroup readOnly value={vend.vendCode} />
                </FormGroup>
                <FormGroup className="Col-2-3" label={t('vendor_name')}>
                    <InputGroup readOnly value={vend.vendName} />
                </FormGroup>
            </div>
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('payable')}>
                    <InputGroup readOnly value={vend.acPayable} />
                </FormGroup>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <Button className="Col-1-1" icon="trash" onClick={() => setIsOpenDelete(!isOpenDelete)}>{t('delete')}</Button>
            </div>
        </div>
    )
}

interface VendorEditProps {
    vend?: Vendor,
    isNew?: boolean,
    onSave?: (vend: Vendor) => void,
    onCancel?: () => void;
}

function VendorEdit({ vend = {} as Vendor, isNew = false, onSave = () => { }, onCancel = () => { } }: VendorEditProps) {
    const { t, i18n } = useTranslation();
    const { register, handleSubmit, reset, setValue } = useForm<Vendor>()

    useEffect(() => {
        // Call reset to set default values if vend is passed
        if ("vendCode" in vend) {
            reset(vend)
        }
    }, [vend, reset])

    useEffect(() => {
        // Suggest don't have ref to use with react-hook-form, so register it manually and call setValue when item is selected
        //register({ name: "acType" })
    }, [register])

    if (!isNew && !("vendCode" in vend)) return null

    const onSubmit = (data: Vendor) => {
        //console.log("onsubmit called", data)
        onSave(data)
    }

    function highlightText(text: string, query: string) {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens: React.ReactNode[] = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    }

    function escapeRegExpChars(text: string) {
        //return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return text.replace(/([.*+?^=!:${}()|\\])/g, "\\$1");
    }

    const handleHotkey = (e: React.KeyboardEvent<HTMLFormElement>) => {
        //console.log(`ctrl:${e.ctrlKey} alt:${e.altKey} shift:${e.shiftKey} meta:${e.metaKey} key:${e.key} keyCode:${e.keyCode}`)

        // ctrl + s
        if (e.ctrlKey && e.keyCode === 83) {
            e.preventDefault()
            handleSubmit(onSubmit)()
        }
    }

    return (
        <form onKeyDown={handleHotkey}>
            <div className="Row">
                <Button className="Col-1-1" icon="cross" onClick={() => onCancel()}>{t('cancel')}</Button>
                <Button className="Col-2-1" icon="tick" onClick={handleSubmit(onSubmit)}>{t('save')}</Button>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('vendor')} labelFor="inputVendCode">
                    <InputGroup readOnly={!isNew} id="inputVendCode" name="vendCode" inputRef={register} />
                </FormGroup>
                <FormGroup className="Col-2-3" label={t('vendor_name')} labelFor="inputVendName">
                    <InputGroup id="inputVendName" name="vendName" inputRef={register} />
                </FormGroup>
            </div>
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('payable')} labelFor="inputPayable">
                    <InputGroup id="inputPayable" name="acPayable" inputRef={register} />
                </FormGroup>
            </div>
        </form>
    )
}

interface VendorMainProps {
    svc: AxiosInstance
}

function VendorMain({ svc }: VendorMainProps) {
    let { tabId: defaultTabId = "list" }: { tabId: string } = useParams()
    const { t, i18n } = useTranslation();
    const history = useHistory()

    const [data, setData] = useState([] as Vendor[])

    const [searchKey, setSearchKey] = useState("vendCode")
    const [searchValue, setSearchValue] = useState("")

    const [showFilters, setShowFilters] = useState(true)

    const [searchName, setSearchName] = useState("")
    const [searchType, setSearchType] = useState("")
    const [searchCcy, setSearchCcy] = useState("")

    const [searchLimit, setSearchLimit] = useState(0)
    const [searchBackward, setSearchBackward] = useState(false)

    const [selectedTabId, setSelectedTabId] = useState(defaultTabId) //useState("list")
    const [lastTabId, setLastTabId] = useState(defaultTabId) //useState("list")
    const [currentVend, setCurrentVend] = useState({} as Vendor)

    const [currentMode, setCurrentMode] = useState("show")

    useEffect(() => {
        setSelectedTabId(defaultTabId)
    }, [defaultTabId])

    const changeTab = (tabId: string) => {
        history.push(`/vendors/${tabId}`)
        setSelectedTabId(tabId)
    }

    useEffect(() => {
        (async () => {
            refreshVendors()
        })()
    }, [svc])

    useEffect(() => {
        if (data.length > 0) {
            let i = 0;
            if ("vendCode" in currentVend) {
                i = data.findIndex(vend => vend.vendCode === currentVend.vendCode)
            }
            setCurrentVend(i > 0 ? data[i] : data[0])
        }
    }, [data])

    const refreshVendors = async () => {
        try {
            const res = await svc.post('/vendorlist',
                {
                    by: searchKey,
                    start: searchValue,
                    limit: searchLimit,
                    backward: searchBackward,
                    filters: {
                        vendName: searchName,
                        acType: searchType,
                        ccyCode: searchCcy
                    }
                });

            setData(res.data.data)
            if (res.data.data.length === 0) {
                utils.showError(t('no_record'))
            }
        } catch (error) {
            utils.showError(error)
        }    
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        refreshVendors()
    }

    const editOnSave = async (vend: Vendor) => {
        try {
            const res = await svc.post('/vendorupdate', vend)
            utils.showSuccess(t('saved', {key: res.data.data.vendCode}))
            for (let i = 0; i < data.length; i++) {
                if (data[i].vendCode === res.data.data.vendCode) {
                    data[i] = { ...data[i], ...res.data.data }
                    setData(data)
                    break
                }
            }
            setCurrentVend(res.data.data)
            setCurrentMode("show")
        } catch (error) {
            utils.showError(error)
        }
    }

    const newOnSave = async (vend: Vendor) => {
        try {
            const res = await svc.post('/vendoradd', vend)
            utils.showSuccess(t('saved', {key: res.data.data.vendCode}))
            refreshVendors()
            setCurrentVend(res.data.data)
            setCurrentMode("show")
        } catch (error) {
            utils.showError(error)
        }
    }

    const viewOnDelete = async (vend: Vendor) => {
        try {
            //console.log("deleting vendor:", vend)
            const res = await svc.post('/vendordelete', vend)
            utils.showSuccess(t('deleted'))
            setData(data.filter(vend => !(vend.vendCode === res.data.vendCode)))
            setCurrentVend({} as Vendor)
            changeTab("list")
        } catch (error) {
            utils.showError(error)
        }
    }

    // currentMode = show
    let detail = <VendorView vend={currentVend} baseUrl={svc.defaults.baseURL} token={localStorage.getItem('token')} onEdit={() => setCurrentMode("edit")} onNew={() => {setLastTabId(selectedTabId); setCurrentMode("new")}} onDelete={viewOnDelete}></VendorView>
    switch (currentMode) {
        case "edit":
            detail = <VendorEdit vend={currentVend} onSave={editOnSave} onCancel={() => setCurrentMode("show")}></VendorEdit>
            break
        case "new":
            detail = <VendorEdit isNew onSave={newOnSave} onCancel={() => { setCurrentMode("show"); changeTab(lastTabId) }}></VendorEdit>
            break
    }

    let list = <>
        <div className="Row">
            <Button className="Col-1-1" icon="add" onClick={() => { setLastTabId(selectedTabId); setCurrentMode("new"); changeTab("detail") }}>{t('new')}</Button>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-ACCode">{t('vendor')}</th>
                    <th className="TCol-ACName">{t('vendor_name')}</th>
                    <th className="TCol-ACCode">{t('payable')}</th>
                </tr>
            </thead>
            <tbody>
                {data.map((vend, i) =>
                    <tr key={i} onDoubleClick={() => { changeTab("detail") }}>
                        <td className="TCol-ACCode" onClick={() => { setCurrentVend(vend) }}>{vend.vendCode}</td>
                        <td className="TCol-ACName" onClick={() => { setCurrentVend(vend) }}>{vend.vendName}</td>
                        <td className="TCol-ACCode" onClick={() => { setCurrentVend(vend) }}>{vend.acPayable}</td>
                    </tr>
                )}
            </tbody>
        </HTMLTable>
    </>

    return (
        <>
            <div style={{fontSize: "21px", fontWeight: 700}}>{t('vendors')}</div>
            <div className="Spacing-V-16"></div>
            <form onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-3" label={t('search')} labelFor="inputSearchValue">
                        <ControlGroup fill>
                            <HTMLSelect className={Classes.FIXED} value={searchKey} onChange={utils.handleStringChange(s => setSearchKey(s))}>
                                <option value="vendCode">{t('vendor')}</option>
                                <option value="_recordNo">Record No.</option>
                            </HTMLSelect>
                            <InputGroup
                                id="inputSearchValue"
                                placeholder={t('search')}
                                value={searchValue}
                                onChange={utils.handleStringChange(s => setSearchValue(s))}
                                leftIcon="search"
                                rightElement={<Button icon={IconNames.ARROW_RIGHT} minimal type="submit"></Button>}
                            />
                        </ControlGroup>
                    </FormGroup>
                    {/*<Checkbox className="Col-4-1 Checkbox-LabeledFormGroup" label="Backward" onChange={utils.handleBooleanChange(v => setSearchBackward(v))} />*/}
                    {/*<Button className="Col-5-1 Button-LabeledFormGroup" icon="filter" onClick={() => { setShowFilters(!showFilters) }} >Filters</Button>*/}
                </div>
                <Collapse isOpen={showFilters}>
                    <div className="Row">
                        <FormGroup className="Col-1-3" label={t('vendor_name')} labelFor="searchName">
                            <InputGroup id="searchName" value={searchName} onChange={utils.handleStringChange(s => setSearchName(s))} />
                        </FormGroup>
                        {/*
                        <div className="Col-4-2 Col-H">
                            <FormGroup label="Type" labelFor="searchType">
                                <InputGroup className="Input-XS" id="searchType" value={searchType} onChange={utils.handleStringChange(s => setSearchType(s))} />
                            </FormGroup>
                            <FormGroup label="Limit" labelFor="searchLimit" >
                                <InputGroup className="Input-XS" id="searchLimit" value={searchLimit.toString()} onChange={utils.handleNumberChange(n => setSearchLimit(n))} />
                            </FormGroup>
                        </div>
                        */}
                    </div>
                </Collapse>
            </form>
            <Tabs onChange={(newTabId) => changeTab(newTabId as string)} selectedTabId={selectedTabId}>
                <Tab id="list" title={t('list')} panel={list} />
                <Tab id="detail" title={t('detail')} panel={detail} />
            </Tabs>
        </>
    );
}

export default VendorMain